import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Modal, Input, Select, Button, EmptyState } from "@/admin/ui";
import { toast } from "sonner";
import { ROLE_LABEL } from "@/user/dashboards.config";
import { formsForRole } from "@/user/workspace/forms.config";
import SubmissionForm from "@/user/workspace/SubmissionForm";
import { PageHeader, StatusPill, PriorityPill } from "@/user/workspace/wsui";
import { ws, fmtDate, fmtBytes, downloadAttachment } from "@/user/workspace/ws.api";

export default function Submissions() {
    const { user } = useOutletContext();
    const forms = formsForRole(user.role);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", status: "", priority: "", type: "", date_from: "", date_to: "" });
    const [detail, setDetail] = useState(null);
    const [editForm, setEditForm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
            setRows(await ws.listSubmissions(params));
        } catch {
            toast.error("Could not load submissions");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    async function openDetail(id) {
        try { setDetail(await ws.getSubmission(id)); }
        catch { toast.error("Could not open submission"); }
    }

    async function onDelete(s) {
        if (!window.confirm(`Delete ${s.submission_id}? This cannot be undone.`)) return;
        try {
            await ws.deleteSubmission(s.id);
            toast.success("Submission deleted");
            setDetail(null);
            load();
        } catch { toast.error("Delete failed"); }
    }

    async function onSubmitDraft(s) {
        try {
            await ws.submitSubmission(s.id);
            toast.success("Submitted");
            setDetail(null);
            load();
        } catch { toast.error("Submit failed"); }
    }

    async function onEdit(s) {
        const def = forms.find((f) => f.key === s.type);
        if (!def) { toast.error("This form type is not editable here"); return; }
        const full = await ws.getSubmission(s.id);
        setDetail(null);
        setEditForm({ def, submission: full });
    }

    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    return (
        <div data-testid="page-submissions">
            <PageHeader
                eyebrow={ROLE_LABEL[user.role] || user.role}
                title="My Submissions"
                subtitle="Your complete submission timeline, drafts and history — filter, review, edit."
            />

            <div className="glass rounded-2xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                <div className="lg:col-span-2">
                    <Input placeholder="Search title / ID / notes" value={filters.q} onChange={(e) => set("q", e.target.value)} data-testid="sub-search" />
                </div>
                <Select value={filters.status} onChange={(e) => set("status", e.target.value)} data-testid="sub-filter-status">
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="submitted">Submitted</option>
                    <option value="updated">Updated</option>
                </Select>
                <Select value={filters.priority} onChange={(e) => set("priority", e.target.value)} data-testid="sub-filter-priority">
                    <option value="">All priorities</option>
                    {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
                <Select value={filters.type} onChange={(e) => set("type", e.target.value)} data-testid="sub-filter-type">
                    <option value="">All types</option>
                    {forms.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </Select>
                <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="sub-filter-from" />
            </div>

            {loading ? (
                <div className="text-neutral-500 py-16 text-center">Loading…</div>
            ) : rows.length === 0 ? (
                <EmptyState title="No submissions yet" hint="Head to My Workspace to create your first update." />
            ) : (
                <div className="glass rounded-2xl divide-y divide-black/5 overflow-hidden" data-testid="sub-list">
                    {rows.map((s) => (
                        <button key={s.id} onClick={() => openDetail(s.id)} data-testid={`sub-row-${s.id}`} className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-3.5 hover:bg-black/[0.02] text-left">
                            <div className="min-w-0">
                                <div className="text-[14px] font-medium text-black truncate">{s.title}</div>
                                <div className="text-[11px] text-neutral-400 font-mono-alt mt-0.5">{s.submission_id} · {fmtDate(s.updated_at)}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <PriorityPill priority={s.priority} />
                                <StatusPill status={s.status} />
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Detail modal */}
            <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.title || "Submission"} size="lg" testid="sub-detail">
                {detail && (
                    <div>
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            <span className="text-[11px] font-mono-alt text-neutral-400">{detail.submission_id}</span>
                            <StatusPill status={detail.status} />
                            <PriorityPill priority={detail.priority} />
                            <span className="text-[11px] text-neutral-400">Created {fmtDate(detail.created_at)}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            {Object.entries(detail.data || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                                <div key={k} data-testid={`detail-field-${k}`}>
                                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400">{k.replace(/_/g, " ")}</div>
                                    <div className="text-[14px] text-black mt-0.5 break-words">{String(v)}</div>
                                </div>
                            ))}
                        </div>
                        {detail.notes && (
                            <div className="mt-4">
                                <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400">Notes</div>
                                <div className="text-[14px] text-black mt-0.5">{detail.notes}</div>
                            </div>
                        )}
                        {detail.tags?.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-1.5">
                                {detail.tags.map((t) => <span key={t} className="text-[11px] bg-neutral-100 text-neutral-600 rounded-full px-2 py-0.5">#{t}</span>)}
                            </div>
                        )}
                        {detail.attachments?.length > 0 && (
                            <div className="mt-5">
                                <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-2">Attachments</div>
                                <ul className="space-y-2">
                                    {detail.attachments.map((a) => (
                                        <li key={a.id} className="flex items-center justify-between rounded-xl bg-neutral-50 border border-black/5 px-3 py-2">
                                            <span className="text-[13px] text-black truncate">{a.filename}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-[11px] text-neutral-400">{fmtBytes(a.size)}</span>
                                                <button className="text-violet-600 hover:text-violet-700 text-[12px]" data-testid={`detail-download-${a.id}`}
                                                    onClick={async () => { const full = await ws.getAttachment(a.id); downloadAttachment(full); }}>
                                                    Download
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="mt-7 flex items-center justify-end gap-2 border-t border-black/5 pt-4">
                            <Button variant="danger" size="sm" onClick={() => onDelete(detail)} data-testid="detail-delete">Delete</Button>
                            <Button variant="ghost" size="sm" onClick={() => onEdit(detail)} data-testid="detail-edit">Edit</Button>
                            {detail.status === "draft" && (
                                <Button variant="primary" size="sm" onClick={() => onSubmitDraft(detail)} data-testid="detail-submit">Submit Now</Button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {editForm && (
                <SubmissionForm
                    form={editForm.def}
                    role={user.role}
                    editing={editForm.submission}
                    open={!!editForm}
                    onClose={() => setEditForm(null)}
                    onSaved={load}
                />
            )}
        </div>
    );
}
